<template>
  <div>
    <header-component />
    <div class="container-fluid mt--6">
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h3 class="mb-0">Staff Members</h3>
          <input
            v-model="search"
            class="form-control form-control-sm"
            style="width: 220px"
            placeholder="Search by name..."
            @input="onSearch"
          />
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Branch</th>
                <th class="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="5" class="text-center py-10 text-gray-400">Loading...</td>
              </tr>
              <tr v-else-if="staff.length === 0">
                <td colspan="5" class="text-center py-10 text-gray-400">No staff members found.</td>
              </tr>
              <tr
                v-for="row in staff"
                :key="row.user_id"
                class="hover:bg-gray-50 border-b"
              >
                <td class="px-4 py-3 text-sm text-gray-800">{{ row.name }}</td>
                <td class="px-4 py-3">
                  <span class="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {{ row.role_label }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-700" style="white-space: nowrap">{{ row.phone }}</td>
                <td class="px-4 py-3 text-sm text-gray-700">
                  <span
                    v-if="row.branch_name"
                    class="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full"
                    :style="branchBadgeStyle(row.branch_name)"
                  >{{ row.branch_name }}</span>
                  <span v-else>—</span>
                </td>
                <td class="px-4 py-3 text-right">
                  <el-dropdown @command="command => handleAction(row, command)">
                    <el-button type="primary" size="mini">
                      Actions
                      <i class="el-icon-arrow-down el-icon--right" />
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="edit">Edit</el-dropdown-item>
                      <el-dropdown-item command="setPassword">Set Password</el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div
      v-if="passwordModal"
      class="d-flex align-items-center justify-content-center"
      style="position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.45);z-index:9999;"
    >
      <div class="bg-white rounded shadow-lg p-4" style="width:360px;max-width:95vw;">
        <h5 class="mb-3">Set Password — {{ passwordUser && passwordUser.name }}</h5>
        <div class="form-group mb-2">
          <label class="form-control-label">New Password</label>
          <input
            v-model="newPassword"
            type="password"
            class="form-control form-control-sm"
            placeholder="Min 6 characters"
            @keyup.enter="savePassword"
          />
        </div>
        <div class="form-group mb-3">
          <label class="form-control-label">Confirm Password</label>
          <input
            v-model="confirmPassword"
            type="password"
            class="form-control form-control-sm"
            placeholder="Repeat password"
            @keyup.enter="savePassword"
          />
        </div>
        <p v-if="passwordError" class="text-danger text-sm mb-2">{{ passwordError }}</p>
        <div class="d-flex">
          <button type="button" class="btn btn-secondary btn-sm flex-fill mr-2" @click="closePasswordModal">Cancel</button>
          <button type="button" class="btn btn-primary btn-sm flex-fill" :disabled="savingPassword" @click="savePassword">
            {{ savingPassword ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Dropdown, DropdownMenu, DropdownItem, Button } from "element-ui";
import { mapActions } from "vuex";
import { apiHelper } from "@/utils/helpers";
import endpoints from "@/utils/constants";
import headerComponent from "../Components/HeaderComponent";

export default {
  components: {
    "header-component": headerComponent,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Button.name]: Button,
  },
  data() {
    return {
      staff: [],
      allBranches: [],
      search: "",
      loading: false,
      searchTimeout: null,
      passwordModal: false,
      passwordUser: null,
      newPassword: "",
      confirmPassword: "",
      passwordError: "",
      savingPassword: false,
    };
  },
  mounted() {
    this.fetchBranches();
    this.fetchStaff();
  },
  methods: {
    ...mapActions({ fetchUser: "user/fetchUser" }),

    fetchBranches() {
      apiHelper
        .call("get", `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`)
        .then((res) => { this.allBranches = res.data.data || res.data; })
        .catch(() => {});
    },

    branchBadgeStyle(branchName) {
      const b = this.allBranches.find((x) => x.name === branchName);
      if (!b || !b.color) return { background: '#dbeafe', color: '#1e40af' };
      return { background: b.color, color: '#fff' };
    },

    fetchStaff() {
      this.loading = true;
      const url = `${endpoints.LARAVEL_BASE_URL}/api/v1/staff-users${this.search ? `?search=${encodeURIComponent(this.search)}` : ""}`;
      apiHelper
        .call("get", url)
        .then((res) => {
          this.staff = res.data.users || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.fetchStaff(), 350);
    },

    handleAction(row, command) {
      if (command === "edit") {
        this.editStaff(row);
      } else if (command === "setPassword") {
        this.openPasswordModal(row);
      }
    },

    editStaff(row) {
      this.fetchUser(row.user_id).then(() => {
        this.$router.push("/users/add/" + row.user_id);
      });
    },

    openPasswordModal(row) {
      this.passwordUser = row;
      this.newPassword = "";
      this.confirmPassword = "";
      this.passwordError = "";
      this.passwordModal = true;
    },

    closePasswordModal() {
      this.passwordModal = false;
      this.passwordUser = null;
      this.newPassword = "";
      this.confirmPassword = "";
      this.passwordError = "";
    },

    savePassword() {
      this.passwordError = "";
      if (!this.newPassword || this.newPassword.length < 6) {
        this.passwordError = "Password must be at least 6 characters.";
        return;
      }
      if (this.newPassword !== this.confirmPassword) {
        this.passwordError = "Passwords do not match.";
        return;
      }
      this.savingPassword = true;
      apiHelper
        .call("patch", `${endpoints.LARAVEL_BASE_URL}/api/v1/users/${this.passwordUser.user_id}/password`, {
          password: this.newPassword,
          password_confirmation: this.confirmPassword,
        })
        .then(() => {
          if (window.notify) {
            window.notify({ message: "Password updated successfully.", type: "success", title: "Done" });
          }
          this.closePasswordModal();
        })
        .catch((err) => {
          const msg = (err && err.response && err.response.data && err.response.data.message)
            || "Failed to update password.";
          this.passwordError = msg;
        })
        .finally(() => {
          this.savingPassword = false;
        });
    },
  },
};
</script>
